import { createContext, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import cookies from "js-cookie";
import { v4 as uuidv4 } from "uuid";

// Components
import Languages from "./components/Main/Languages";
import MainTextTop from "./components/Main/MainTextTop";
import FamilyInputs from "./components/Main/FamilyInputs";

// Other Local Imports
import "./css/App.css";
import { People, Household } from "./models";
import { sampleHouseHold } from "./initialStates";

// Popups
import Popup from "./components/Main/PersonPopout/Popup";
import SubmittedPopup from "./components/Main/SubmittedPopup";
import ErrorPopup from "./components/Main/ErrorPopup";
import API from "./API";

interface AppContextInterface {
  handlePeopleChange: (changes: any) => void;
}

export const ContextMobile = createContext<AppContextInterface | null>(null);

const HomePage: React.FC = () => {
  const { t } = useTranslation("ns1");
  const [popup, setPopup] = useState<boolean>(false);
  const [submittedPopup, setSubmittedPopup] = useState<boolean>(false);
  const [people, setPeople] = useState<People[]>([]);
  const [selectedPersonId, setSelectedPersonId] = useState<string>("");
  const [selectedPersonIndex, setSelectedPersonIndex] = useState<number>(0);
  const [household, setHousehold] = useState<Household>(sampleHouseHold);
  const [errorPopup, setErrorPopup] = useState<boolean>(false);
  const [required, setRequired] = useState<boolean>(false);
  const [manPants, setManPants] = useState<boolean>(false);

  const houseInfoRef = useRef<any>();

  // Turn right to left for dari
  const currentLanguageCode = cookies.get("i18next") || "en";

  useEffect(() => {
    currentLanguageCode === "dari"
      ? (document.body.dir = "rtl")
      : (document.body.dir = "ltr");
  }, [currentLanguageCode]);

  const handleSubmitOrder = async () => {
    // Check required fields
    const inputs: HTMLInputElement[] = [
      ...houseInfoRef.current.querySelectorAll("input"),
    ];

    const allValid = inputs.every((input: HTMLInputElement) =>
      input.reportValidity()
    );

    if (allValid) {
      // 1) Submit the order to the api

      const totalPackage = { household, people };

      API.processOrder(totalPackage).then((res) => {
        if (res.status === 201) {
          // 2) Clear the state
          setPeople([]);
          setHousehold(sampleHouseHold);

          // 3) Popout that says Completed
          setSubmittedPopup(true);
        } else {
          console.log(res.body);
          setErrorPopup(true);
          setRequired(false);
        }
      });
    } else {
      setRequired(true);
    }
  };

  const handleHouseHoldChange = (changes: any) => {
    const newHousehold = { ...household, ...changes };
    setHousehold(newHousehold);
  };

  const handlePeopleChange = (changes: any) => {
    const newPeople = [...people];
    const index = newPeople.findIndex((r) => r.id === selectedPersonId);
    newPeople[index] = { ...newPeople[index], ...changes };
    setPeople(newPeople);
  };

  const handleAddPerson = () => {
    const newPerson = {
      id: uuidv4(),
      CA: "",
      first_name: "",
      age: "",
      height_feet: "",
      height_inches: "",
      MF: "feet",
      sex: "",
      shirt_size: "",
      shoe_size: "",
      inseam_length: "",
      waist: "",
      bra_size: "",
      pants_size: "",
      clothing_styles: "",
      shoe_types: "",
      color_preferences: "",
      anything_else: "",
    };

    setSelectedPersonIndex(people.length);
    setPeople([...people, newPerson]);
    setSelectedPersonId(newPerson.id);
    setPopup(true);
  };

  const handleEdit = (id: string, index: number) => {
    setSelectedPersonIndex(index);
    setSelectedPersonId(id);

    setPopup(true);
  };

  const handleRemove = (id: string) => {
    const newPeople = [...people];
    const index = newPeople.findIndex((r) => r.id === id);
    newPeople.splice(index, 1);
    setPeople(newPeople);
  };

  const contextValue = {
    handlePeopleChange,
    people,
    selectedPersonIndex,
    required,
    setRequired,
    manPants,
  };

  return (
    <ContextMobile.Provider value={contextValue}>
      <div className="app">
        <Languages />
        <MainTextTop t={t} />
        <FamilyInputs
          household={household}
          handleHouseHoldChange={handleHouseHoldChange}
          houseInfoRef={houseInfoRef}
          required={required}
        />

        <p>{t("please_add")}</p>
        <div
          className="btn add_person_button"
          onClick={() => handleAddPerson()}
        >
          {people.length === 0 ? t("add_person") : t("add_another_person")}
        </div>

        {people.map((p, index) => {
          return (
            <div key={p.id}>
              {
                <div id="people-container">
                  <div className="people-container-child">{index + 1})</div>
                  <div id="firstName" className="people-container-child">
                    {p.first_name}
                  </div>

                  <div
                    id="remove-btn"
                    className="people-container-child"
                    onClick={() => handleEdit(p.id, index)}
                  >
                    ({t("edit")})
                  </div>
                  <div
                    id="remove-btn"
                    className="people-container-child"
                    onClick={() => handleRemove(p.id)}
                  >
                    ({t("remove")})
                  </div>
                </div>
              }
            </div>
          );
        })}
        {people.length !== 0 && (
          <div
            className="btn submit_button"
            onClick={() => handleSubmitOrder()}
          >
            {t("submit_request")}
          </div>
        )}

        {popup && (
          <Popup
            setPopup={setPopup}
            people={people}
            selectedPersonIndex={selectedPersonIndex}
            t={t}
            required={required}
            setRequired={setRequired}
            setManPants={setManPants}
            manPants={manPants}
          />
        )}
        {submittedPopup && (
          <SubmittedPopup setSubmittedPopup={setSubmittedPopup} />
        )}
        {errorPopup && <ErrorPopup setErrorPopup={setErrorPopup} />}
      </div>
      <div className="footer">
        <div>
          {t("beth_el_1")}{" "}
          <a
            href="https://bethelmennonite.org/"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("beth_el_2")}
          </a>
          {t("beth_el_3")}
          <a href="mailto: mobileclothes@bmccs.org">{t("beth_el_4")}</a>
          {"."}
          <br />
          <br />
          {t("beth_el_5")}
        </div>
      </div>
    </ContextMobile.Provider>
  );
};

export default HomePage;
